import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { isSudoRequired, SudoCancelledError } from './authErrors';

// Lazy-loaded: the step-up modal is shown only when a sudo-gated action hits a
// 403, so it stays out of the initial bundle (keeps the JS gzip budget) and its
// chunk fetches on first use. Named → default for React.lazy.
const SudoModal = lazy(() =>
  import('./SudoModal').then((m) => ({ default: m.SudoModal })),
);

// Runs `action`, and if it fails the §1.11 sudo gate (403 `sudo_required`), opens
// the SudoModal, awaits a successful `POST /api/sudo`, then TRANSPARENTLY RETRIES
// the same action and resolves with its result — the caller never sees the 403.
// If the user cancels, rejects with `SudoCancelledError` (a benign abort; the
// action was not performed). Any non-sudo error propagates unchanged.
export type RunWithSudo = <T>(action: () => Promise<T>) => Promise<T>;

interface SudoContextValue {
  runWithSudo: RunWithSudo;
}

// Default is a transparent passthrough: outside a `<SudoProvider>` (isolated
// unit tests), `runWithSudo` just runs the action — a `sudo_required` propagates
// unchanged, exactly as before the modal existed. The real app always mounts the
// provider at the root (main.tsx), so the modal is universally available there.
const SudoContext = createContext<SudoContextValue>({
  runWithSudo: (action) => action(),
});

// One pending elevation at a time — the modal is focus-trapped, so a second
// gated action can't be triggered while it's open. `resolve`/`reject` bridge the
// modal's Confirm/Cancel back to the awaiting `runWithSudo` call.
interface PendingElevation {
  resolve: () => void;
  reject: (err: unknown) => void;
}

export function SudoProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingElevation | null>(null);
  // Hold the live resolver in a ref too, so the modal callbacks always settle the
  // current promise even across re-renders.
  const pendingRef = useRef<PendingElevation | null>(null);

  const elevate = useCallback((): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const p: PendingElevation = { resolve, reject };
      pendingRef.current = p;
      setPending(p);
    });
  }, []);

  const runWithSudo = useCallback<RunWithSudo>(
    async (action) => {
      try {
        return await action();
      } catch (err) {
        if (!isSudoRequired(err)) throw err;
        // Gated: open the modal and wait for a successful re-auth (or a cancel,
        // which throws SudoCancelledError out of `elevate`).
        await elevate();
        // Sudo window is now fresh — retry the original action transparently.
        return await action();
      }
    },
    [elevate],
  );

  const settle = useCallback((outcome: 'confirmed' | 'cancelled') => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    if (!p) return;
    if (outcome === 'confirmed') p.resolve();
    else p.reject(new SudoCancelledError());
  }, []);

  return (
    <SudoContext.Provider value={{ runWithSudo }}>
      {children}
      {pending && (
        <Suspense fallback={null}>
          <SudoModal
            onConfirmed={() => settle('confirmed')}
            onCancel={() => settle('cancelled')}
          />
        </Suspense>
      )}
    </SudoContext.Provider>
  );
}

export function useSudo(): SudoContextValue {
  return useContext(SudoContext);
}
